import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  UpAutocompleteComponent,
  DropdownChipsComponent,
  HereMapComponent,
  ContactDetailsOverlayComponent,
  LocationExternalAutocompleteComponent,
} from '@loup-transload-ui-app/common-components';
import { TransloadFacilityService } from '../../api/external-web/transload-facility.service';
import { CommonHelperService } from '@loup-transload-ui-app/common-services';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-transload-facility-search-map',
  imports: [
    CommonModule,
    LocationExternalAutocompleteComponent,
    DropdownChipsComponent,
    HereMapComponent,
    ContactDetailsOverlayComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './transload-facility-search-map.component.html',
  styleUrl: './transload-facility-search-map.component.scss',
})
export class TransloadFacilitySearchMapComponent implements OnInit {
  private readonly transloadFacilityService = inject(TransloadFacilityService);
  private readonly commonHelper = inject(CommonHelperService);
  private destroyRef = inject(DestroyRef);
  private breakpointObserver = inject(BreakpointObserver);
  @ViewChild('cityStateDropdown') cityStateDropdown: UpAutocompleteComponent;
  @ViewChild('dropdownChip') dropdownChip: DropdownChipsComponent;
  public searchBy = 'Search by City State';
  public fieldName = 'Radius';
  public contactOverlay = false;
  public options = [25, 50, 100, 200, 500];
  public cityState: any = {};
  public radiusMiles = 100;
  public pinsList: any = [];
  public isSubmitted = false;
  public mapHeight = '0px';
  smallScreen = false;

  ngOnInit(): void {
    this.dynamicHeight();
    this.getDefaultData();
    window.addEventListener('resize', this.resizeListener);
    window.removeEventListener('resize', this.resizeListener);
  }

  private resizeListener = () => {
    this.dynamicHeight();
  };

  dynamicHeight() {
    this.smallScreen = false;
    this.breakpointObserver
      .observe([
        '(max-width: 599px)' /** mobile */,
        '(min-width: 600px) and (max-width: 1023px)' /** tablet */,
        '(min-width: 1024px) and (max-width: 1599px)' /** small desktop */,
        '(min-width: 1600px)' /** large desktop */,
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        const h = this.getViewportHeight();

        if (state.breakpoints['(max-width: 599px)']) {
          /** Mobile: fixed height */
          this.mapHeight = '1400px';
        } else if (
          state.breakpoints['(min-width: 600px) and (max-width: 1023px)']
        ) {
          /** Tablet: dynamic */
          this.mapHeight = `${Math.round(h * 0.8)}px`;
        } else if (
          state.breakpoints['(min-width: 1024px) and (max-width: 1599px)']
        ) {
          /** Small Desktop: dynamic */
          this.mapHeight = `800px`;
          this.smallScreen = true;
        } else {
          /** Large Desktop: dynamic (adjust factor as you prefer) */
          this.mapHeight = `${Math.round(h * 0.75)}px`;
        }
      });
  }

  private getViewportHeight(): number {
    /** Prefer dynamic viewport on browsers that support it */
    const dvh = (window as any).visualViewport?.height ?? window.innerHeight;
    return Math.round(dvh);
  }

  getDefaultData() {
    const requestObj = {
      origin: {
        latitude: 33.88102,
        longitude: -87.351448,
      },
      maxResults: 250,
      pullAllVendors: true,
    };
    this.transloadFacilityService.getTransloadVendors(requestObj).subscribe({
      next: (res: any) => {
        this.pinsList = [...res];
        this.renderDataInMap();
      },
    });
  }

  getSearchData() {
    this.isSubmitted = true;
    if (this.cityState && !this.cityState.city) {
      return;
    }
    if (!this.radiusMiles) {
      this.radiusMiles = 100;
    }
    const requestObj = {
      origin: this.cityState,
      radiusMiles: this.radiusMiles,
    };
    this.transloadFacilityService.getTransloadVendors(requestObj).subscribe({
      next: (res: any) => {
        this.pinsList = [...res];
        this.renderDataInMap();
      },
    });
  }

  renderDataInMap() {
    this.pinsList.forEach((columnObject: any) => {
      let cityState = '';
      if (columnObject.city && columnObject.state) {
        cityState =
          columnObject.city +
          ', ' +
          columnObject.state +
          ', ' +
          columnObject.country;
      }
      columnObject.lat = columnObject.latitude;
      columnObject.lng = columnObject.longitude;
      columnObject.data = {
        name: 'LOUP Managed Facility',
        address: cityState,
      };
    });
  }

  onSelected(event: any, dropdownName: string) {
    if (dropdownName === 'cityState') {
      this.cityState = event;
    } else {
      this.radiusMiles = event;
    }
  }

  selectedIndex: number | null = null;
  onSelectionChanged(i: number | null) {
    this.selectedIndex = i;
  }

  openExternalScreen(): void {
    this.commonHelper.openInNewTab('/create-rate-request');
  }

  openContact() {
    this.contactOverlay = true;
  }

  overlayClosed(event: any) {
    this.contactOverlay = event;
  }
}
